import { FontSize } from 'apps/webcomponents/src/ui/gzIconButton/types';
import { InputWidthValues } from 'apps/webcomponents/src/ui/gzInput';
import { GzInputWebComponent } from 'apps/webcomponents/src/ui/gzInput/component/GzInputWebComponent';
import { debounce } from 'apps/webcomponents/src/utils/event.utils';
import { WEB_COMPONENT_GZ_INPUT } from 'apps/webcomponents/src/webcomponents.constants';
import { Validator } from 'class-validator';
import { LitElement, html, PropertyValues } from 'lit';
import { property, query } from 'lit/decorators.js';
import { NodeData } from '../../../type';
import { ValidatorManager, ValidatorType } from '../../../type/validators';
import { GzNodeDirectorWebComponent } from '../../gzNodeDirector';
import { styles } from '../view/styles';
import { GzIconButtonWebComponent } from '../../../../gzIconButton';
import { NODE_ADDED, NodeRequestConfig } from '../helpers/types';
import { resetTemporaryNode } from '../../../../../networkV2/networkTree/actions/helpers';
import { EventKeyManager } from '../../../../../utils/keys';

export class GzNodeRenameInputWebComponent<T extends object> extends LitElement {
  @property({ type: Object })
  public nodeElement: GzNodeDirectorWebComponent<T>;

  @property({ type: Function })
  public createNodeRequest: (nodeRequestConfig: NodeRequestConfig) => Promise<NodeData>;

  @property({ type: Object })
  public validators: Array<{ [ValidatorType: string]: Array<Validator> }>;

  @query(WEB_COMPONENT_GZ_INPUT)
  private inputComponent: GzInputWebComponent;

  @query('#save_node')
  private saveBtn: GzIconButtonWebComponent;

  @property({ type: Number, attribute: true, reflect: true })
  maxLength = 64;

  @property({ type: Boolean, attribute: true, reflect: true })
  isEnabled = false;

  @property({ type: String, attribute: true, reflect: true })
  placeholder = '';

  static get styles() {
    return styles;
  }

  private debounceRenameHandler = debounce(async inputValue => {
    const node = this.nodeElement.node;
    const { validation } = await ValidatorManager.validate(
      this.validators[ValidatorType.RENAME] || [],
      node!,
      inputValue
    );
    this.nodeElement.setState({ ...this.nodeElement.state, input: inputValue, error: validation });
    validation === null ? (this.saveBtn.disabled = false) : (this.saveBtn.disabled = true);
    this.requestUpdate();
  }, 200);

  update(changedProperties: PropertyValues) {
    super.update(changedProperties);
    const { state: { rename, error } = {} } = this.nodeElement;
    if (rename || this.nodeElement.node.temporaryNode) {
      setTimeout(() => {
        this.inputComponent.inputFocus();
      }, 100);
    }

    if (error) {
      this.inputComponent.markAsInvalid();
    } else {
      this.inputComponent.markAsValid();
    }
  }

  render() {
    const { state: { error } = {} } = this.nodeElement;
    return html`
      <div class="node node--active">
        <div class="rename-container" @click=${event => event.stopPropagation()}>
          <div class="input-container">
            <gz-input
              inputWidth=${InputWidthValues.FULL}
              .value=${this.nodeElement.node.nodeName}
              placeholder="${this.placeholder}"
              hasCounter="true"
              maxLength="${this.maxLength}"
              maxChars="${this.maxLength}"
              .errorTooltipText=${error ? error?.validation : ''}
              @change=${event => this.inputChange(event)}
              @keydown="${this.onKeyDown}"
            ></gz-input>
          </div>
          <span class="d-flex align-items-center justify-space-between buttons-container">
            <gz-icon-button
              id="save_node"
              @click=${() => this.save()}
              icon="ds-icon-notification_success"
              buttonColor="success"
              fontSize=${FontSize.ICON_SIZE_24}
              class="ml-1"
              ?disabled="${!this.isEnabled}"
            ></gz-icon-button>
            <gz-icon-button
              @click=${event => this.reset(event)}
              fontSize=${FontSize.ICON_SIZE_24}
              icon="ds-icon-delete_large"
              buttonColor="secondary"
              class="ml-1"
            >
            </gz-icon-button>
          </span>
        </div>
      </div>
    `;
  }

  private inputChange(event: CustomEvent): void {
    const changedInputValue = event.detail.sourceEvent;
    const { state: { input } = {} } = this.nodeElement;
    if (changedInputValue === input) {
      return;
    }

    this.debounceRenameHandler(changedInputValue);
  }

  private async save(): Promise<void> {
    const { state: { input } = {} } = this.nodeElement;
    const hasNoInput = !input || input === 0;
    const nodeName = this.nodeElement.node.nodeName;

    if (hasNoInput) {
      this.debounceRenameHandler(nodeName);
      return;
    }

    const node = this.nodeElement.node;

    if (node?.temporaryNode) {
      await this.createFinalNode(node);
    } else {
      this.nodeElement.dataSource.renameNode(this.nodeElement, input as string);
    }

    this.nodeElement.setState(null);
    this.requestUpdate();
  }

  private async createFinalNode(temporaryNode: NodeData): Promise<void> {
    const { state: { input } = {} } = this.nodeElement;
    this.createNodeRequest({ nodeData: temporaryNode, changes: { name: input } })
      .then(async (newNode: NodeData) => {
        const parentNode = this.nodeElement.nodes.get(temporaryNode.parentNodeId!);
        await this.nodeElement.dataSource.deleteNode(temporaryNode.nodeId);
        await this.nodeElement.dataSource.attachNodeToContainer(newNode, parentNode!);
        this.nodeElement.dispatchEvent(
          new CustomEvent(NODE_ADDED, {
            detail: {
              nodeData: newNode,
            },
            bubbles: true,
            composed: true,
          })
        );
      })
      .catch(() => {
        this.nodeElement.dataSource.deleteNode(temporaryNode.nodeId);
      });
    resetTemporaryNode(temporaryNode);
  }

  private reset(event: Event): void {
    event.stopPropagation();
    this.nodeElement.setState(null);
    const node = this.nodeElement.node;

    if (node?.temporaryNode) {
      this.nodeElement.dataSource.deleteNode(node.nodeId);
    }
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (EventKeyManager.isEnter(event) && !this.saveBtn.disabled) {
      this.save();
    }
  }
}
